import type { MarketingPanelContent } from "@/pages/authentication/signup/components/MarketingPanel";

import step1Image from "@/assets/images/signup/step1-advisors.png";
import advisorStep1Image from "@/assets/images/signup/step1-advisor-panel.png";
import step2Image from "@/assets/images/signup/step2-matching.png";
import advisorStep3Image from "@/assets/images/signup/step3-advisor-panel.png";
import step4Image from "@/assets/images/signup/step4-availability.png";
import step4CalendarImage from "@/assets/images/signup/step4-calendar.png";
import advisorStep4Image from "@/assets/images/signup/step4-advisor-panel.png";

export const SIGNUP_PANEL_BULLETS = [
  "AI-matched experts curated to your exact goals and career stage",
  "Structured group sessions built around your specific growth areas",
  "A private, trusted space for candid, high-quality guidance",
];

// Shared achiever proof points shown on the marketing panel across all steps.
export const ACHIEVER_PANEL_STATS = [
  { value: "300+", label: "Experts" },
  { value: "2 hrs", label: "Per month" },
  { value: "4.8★", label: "Avg. rating" },
];

export const ACHIEVER_PANEL_TESTIMONIAL = {
  quote:
    "The Expert Program helped me land the Senior Manager role I'd been chasing for two years — in under six months.",
  name: "Priya S.",
  role: "Senior Product Manager",
  initial: "M",
};

// Shared achiever panel heading/description used across steps 1–3.
const ACHIEVER_PANEL_HEADING = "Join as an Achiever";
const ACHIEVER_PANEL_DESCRIPTION =
  "Explore Expert Programs, build a Personal Advisory Board and take structured action toward your next career goal.";

export const STEP1_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Achievers",
  heading: ACHIEVER_PANEL_HEADING,
  description: ACHIEVER_PANEL_DESCRIPTION,
  bullets: SIGNUP_PANEL_BULLETS,
  stats: ACHIEVER_PANEL_STATS,
  testimonial: ACHIEVER_PANEL_TESTIMONIAL,
  stepInfo: {
    currentStep: 1,
    totalSteps: 3,
    stepLabel: "Create Account",
  },
};

export const PROGRAM_JOIN_STEP1_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Achievers",
  heading: "Join Your Expert Program",
  description:
    "You've been invited to join an expert-led program. Complete your account setup to get started.",
  image: step1Image,
  bullets: SIGNUP_PANEL_BULLETS,
};

export const PROGRAM_JOIN_STEP2_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Achievers",
  heading: "One last step to join",
  description:
    "Complete your professional profile and you'll be added to the program right away.",
  image: step2Image,
  bullets: SIGNUP_PANEL_BULLETS,
  stepInfo: {
    currentStep: 2,
    totalSteps: 2,
    stepLabel: "Professional Profile",
  },
};

const ADVISOR_BULLETS = [
  "Flexible commitment — about 2 hours per month",
  "AI-matched with high-potential achievers in your specific field",
  "Build your leadership network and create a lasting advisory legacy",
];

export const ADVISOR_STEP1_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Experts",
  heading: "Share your expertise. Leave a lasting impact.",
  description:
    "Advise driven professionals through structured programs that fit your schedule and amplify your leadership legacy.",
  image: advisorStep1Image,
  bullets: ADVISOR_BULLETS,
};

export const ADVISOR_STEP2_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Experts",
  heading: "Your experience is your greatest asset",
  description:
    "A detailed profile connects you to achievers who will benefit most from your specific background and industry depth.",
  image: step2Image,
  bullets: ADVISOR_BULLETS,
  stepInfo: {
    currentStep: 2,
    totalSteps: 4,
    stepLabel: "Professional Profile",
  },
};

export const STEP2_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Achievers",
  heading: ACHIEVER_PANEL_HEADING,
  description: ACHIEVER_PANEL_DESCRIPTION,
  bullets: SIGNUP_PANEL_BULLETS,
  stats: ACHIEVER_PANEL_STATS,
  testimonial: ACHIEVER_PANEL_TESTIMONIAL,
  stepInfo: {
    currentStep: 2,
    totalSteps: 3,
    stepLabel: "Professional Profile",
  },
};

export const ACHIEVER_STEP3_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Achievers",
  heading: ACHIEVER_PANEL_HEADING,
  description: ACHIEVER_PANEL_DESCRIPTION,
  bullets: SIGNUP_PANEL_BULLETS,
  stats: ACHIEVER_PANEL_STATS,
  testimonial: ACHIEVER_PANEL_TESTIMONIAL,
  stepInfo: {
    currentStep: 3,
    totalSteps: 3,
    stepLabel: "Your Growth",
  },
};

export const ADVISOR_STEP3_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Experts",
  heading: "Define how you like to advise",
  description:
    "Your style preferences help us match you with achievers whose needs align perfectly with your strengths.",
  image: advisorStep3Image,
  bullets: ADVISOR_BULLETS,
  // Matches the step bar (EXPERT_SIGNUP_STEP_LABELS in constants/signupSteps).
  stepInfo: {
    currentStep: 3,
    totalSteps: 4,
    stepLabel: "Operator DNA",
  },
};

export const STEP3_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Achievers",
  heading: "Define your ideal advisory relationship",
  description:
    "The more specific your preferences, the more precisely our AI can match you with experts who will move the needle.",
  image: step4Image,
  bullets: SIGNUP_PANEL_BULLETS,
  stepInfo: {
    currentStep: 3,
    totalSteps: 4,
    stepLabel: "Advisory Setup",
  },
};

export const ADVISOR_STEP4_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Experts",
  heading: "Almost ready to make an impact",
  description:
    "Your S.T.A.R. stories show achievers the real outcomes behind your experience — the proof that makes them choose you.",
  image: advisorStep4Image,
  bullets: ADVISOR_BULLETS,
  stepInfo: {
    currentStep: 4,
    totalSteps: 4,
    stepLabel: "STAR Portfolio",
  },
};

export const STEP4_PANEL_CONTENT: MarketingPanelContent = {
  badge: "For Achievers",
  heading: "One last setup for your Advisory Board",
  description:
    "Connecting your calendar ensures sessions happen seamlessly — no back-and-forth scheduling ever needed.",
  image: step4CalendarImage,
  bullets: SIGNUP_PANEL_BULLETS,
  stepInfo: {
    currentStep: 4,
    totalSteps: 4,
    stepLabel: "Availability",
  },
};

/**
 * Sign-in page panel: pitches the Expert track, with an Achiever cross-sell
 * card nested inside it. Built as a function because the cross-sell CTA needs
 * the page's navigate handler.
 */
export const SIGNIN_PANEL_CONTENT = (
  onJoinAsAchiever: () => void,
  onApplyAsExpert: () => void,
): MarketingPanelContent => ({
  badge: "AchieveOS Platform",
  heading: "One system for growth, guidance, and progress.",
  description:
    "One system for growth, guidance, and measurable progress. AchieveOS helps every user move from intention to progress with clarity.",
  bullets: [
    "Share your experience through Programs, Personal Advisory Boards, co-Expert engagements and peer opportunities",
    "Stay aligned through sessions, tasks, and guidance. Flexible commitment, average just 2–4 hours per month",
    "Every Expert account includes an Achiever workspace for your own growth",
  ],
  crossSell: [
    {
      id: "achiever",
      badge: "For Career Builders",
      heading: "Accelerate your career with Expert guidance.",
      bullets: [
        "Build your own expert advisory board",
        "Join cohort programs with peers",
        "Track goals, sessions & milestones",
      ],
      ctaLabel: "Join as an Achiever →",
      onCtaClick: onJoinAsAchiever,
    },
    {
      id: "expert",
      badge: "For Experts",
      heading: "Your expertise deserves a system.",
      bullets: [
        "Package your knowledge into expert-led programs",
        "Support achievers through Personal Advisory Boards, sessions, and goals",
        "Build credibility through S.T.A.R.s, outcomes, and proof",
      ],
      ctaLabel: "Apply as an Expert →",
      onCtaClick: onApplyAsExpert,
    },
  ],
});
